/**
 * Contexte du module gestion-eau : accès + rôles (admin / releveur / client, cumulables).
 * Modèle calqué sur ConstructionContext, mais offline-first (Dexie + getSession,
 * jamais getUser()). Applique le bootstrap « premier admin = propriétaire ».
 */
import React, { createContext, useContext, useEffect, useState, useCallback, useRef, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useAppStore } from '../../../stores/appStore';
import authService from '../../../services/authService';
import { waitForEauSession } from '../services/eauAuth';
import { ensureRolesBootstrap, getClientDomaines, getRolesForUser } from '../services/eauRoleService';
import { forceResyncElecUneFois } from '../services/eauElecService';
import { refreshConfig } from '../services/eauConfigService';
import { pullAll, syncAll } from '../services/eauSync';
import { getPendingEnrollment, processPendingEnrollment } from '../services/eauEnrollmentService';
import {
  claimInvitationForCurrentUser,
  claimPendingTokenInvitation,
  invitationTargetPath,
} from '../services/eauInvitationService';
import type { EauDomaine, EauRole, EauRoles, EauSimulatedClient } from '../types/gestionEau';
import {
  domainesPourRoles,
  domaineStorageKey,
  resolveDomaineActif,
  type EauDomaines,
} from '../utils/domaines';
import { SIMULATABLE_ROLES } from '../constants/simulationRoles';
import { eauDb } from '../db/gestionEauDb';
import { nbAValiderSansAvis } from '../utils/elecFacture';
import { ELEC_FACTURES } from '../utils/domaines';
import type { ElecFactureLocal, ElecValidationLocal } from '../types/gestionEau';

/**
 * État de la session Supabase vis-à-vis du module eau (Phase 1 — fondation identité) :
 *  - 'checking'      : vérification en cours (boot/réseau) — afficher un spinner.
 *  - 'valid'         : session présente ET identité = utilisateur courant du shell.
 *  - 'needs-reauth'  : aucune session lisible alors qu'on est en ligne (ou jamais
 *                      établie hors-ligne) → proposer une reconnexion Google.
 *  - 'mismatch'      : la session Google appartient à un AUTRE compte que celui du
 *                      shell → refuser (ne jamais créer une 2ᵉ identité).
 */
export type EauSessionStatus = 'checking' | 'valid' | 'needs-reauth' | 'mismatch';

interface GestionEauContextType {
  userId: string | null;
  /**
   * Rôles EFFECTIFS consommés par les gardes de route, le filtrage de nav et les
   * écrans. En simulation (admin qui incarne un rôle), ne contient QUE le rôle
   * simulé ; sinon = `realRoles`. Aucun consommateur n'a besoin de changer.
   */
  roles: EauRoles;
  /** Rôles RÉELS de l'utilisateur (jamais altérés par la simulation). */
  realRoles: EauRoles;
  /** true = un admin incarne actuellement un autre rôle (simulation active). */
  isSimulating: boolean;
  /** Rôle simulé courant (null hors simulation). Phase 1 : 'releveur' | 'promoteur'. */
  simulatedRole: EauRole | null;
  /** Villa/compte propriétaire simulé (Phase 2). Non-null uniquement en simulation `client`. */
  simulatedClient: EauSimulatedClient | null;
  /**
   * Périmètre de données de la simulation « Propriétaire » : les `compteurIds` de la
   * villa simulée quand `isSimulating && simulatedRole === 'client'`, sinon `null`.
   * La surface propriétaire re-filtre ses lectures Dexie en mémoire sur ce périmètre
   * (helper `filterByScope`). `null` = aucun filtrage (vue admin/releveur/promoteur).
   */
  dataScope: { compteurIds: string[] } | null;
  /**
   * Incarner un rôle (admin uniquement). Sans effet si l'utilisateur réel n'est pas
   * admin, ou si le rôle n'est pas simulable. Le rôle `client` (Propriétaire) EXIGE un
   * `client` (la villa) : appelé sans lui, l'appel est ignoré.
   */
  setSimulation: (role: EauRole, client?: EauSimulatedClient | null) => void;
  /** Sortir de la simulation et revenir à l'app admin réelle. */
  clearSimulation: () => void;
  /** Accès au module = au moins un rôle (eau OU électricité OU client). */
  hasEauAccess: boolean;
  /** Domaines accessibles (ELEC-CC-01), calculés sur les rôles EFFECTIFS. */
  domaines: EauDomaines;
  /** Domaine actif (mémorisé par utilisateur ; forcé si un seul domaine). */
  domaineActif: EauDomaine;
  /** Change de domaine (sans effet si la personne n'a pas ce domaine). */
  setDomaineActif: (d: EauDomaine) => void;
  /**
   * Lecture seule (Phase 2 — Promoteur) : un promoteur « pur » (sans admin ni releveur)
   * voit tous les écrans mais ne peut RIEN écrire, sauf les seuils d'alerte (via RPC).
   * Les écrans masquent/désactivent leurs contrôles d'écriture quand `isReadOnly === true`.
   * Un admin/releveur garde l'écriture même s'il est aussi promoteur.
   */
  isReadOnly: boolean;
  isLoading: boolean;
  error: string | null;
  /** Fiabilité de la session Supabase pour ce module (cf. EauSessionStatus). */
  sessionStatus: EauSessionStatus;
  /**
   * `true` = les rôles ont été résolus de façon FIABLE (pull serveur OK, ou cache local
   * hors-ligne). `false` = état non résolu (démarrage à froid : pull lent/échoué/timeout).
   * La garde d'accès ne redirige vers /dashboard QUE sur un refus confirmé
   * (`rolesConfirmed && !hasEauAccess`), jamais sur un état non résolu.
   */
  rolesConfirmed: boolean;
  refreshRoles: () => Promise<void>;
  /** Relance complète de la résolution session+rôles (bouton « Réessayer »). */
  retryAccess: () => Promise<void>;
  /** Déclenche une reconnexion Google (même identité que le shell). */
  reauth: () => Promise<void>;
  /**
   * Pastilles des boutons de navigation du module, par chemin (ELEC-CC-03) : factures à
   * valider sans avis de la personne, pour un valideur. Vide sinon.
   */
  navBadges: Record<string, number>;
}

/** Événement émis après une écriture sur les factures (relecture immédiate des pastilles). */
export const EAU_FACTURES_EVENT = 'eau-elec-factures';

const EMPTY_ROLES: EauRoles = {
  admin: false,
  releveur: false,
  client: false,
  promoteur: false,
  releveurElec: false,
  gestionElec: false,
  lectureElec: false,
  validationElec: false,
};

// Persistance de la simulation de rôle (100 % frontend). Réservée à l'admin :
// restaurée seulement après confirmation des rôles ET si l'utilisateur est admin,
// purgée sinon et à la déconnexion. `SIM_CLIENT_KEY` est réservé à la Phase 2.
const SIM_ROLE_KEY = 'eau_sim_role';
const SIM_CLIENT_KEY = 'eau_sim_client';

/**
 * Parse défensif de la villa simulée persistée (`eau_sim_client`). Valide la forme
 * (`EauSimulatedClient`) — un JSON corrompu ou d'une version antérieure du type renvoie
 * `null` (la simulation client ne sera pas restaurée plutôt que d'appliquer un périmètre
 * incohérent). `compteurIds` filtré aux seules chaînes.
 */
function parseSimClient(raw: string | null): EauSimulatedClient | null {
  if (!raw) return null;
  try {
    const o = JSON.parse(raw);
    if (
      o &&
      typeof o.id === 'string' &&
      typeof o.label === 'string' &&
      Array.isArray(o.compteurIds)
    ) {
      return {
        id: o.id,
        userId: typeof o.userId === 'string' ? o.userId : null,
        label: o.label,
        compteurIds: (o.compteurIds as unknown[]).filter((x): x is string => typeof x === 'string'),
      };
    }
  } catch {
    /* JSON invalide */
  }
  return null;
}

/**
 * Rôles EFFECTIFS = si l'utilisateur réel est admin ET qu'un rôle est simulé, un
 * objet où SEUL le rôle simulé est `true` ; sinon les rôles réels inchangés. La
 * garde `real.admin` est un filet : un non-admin ne peut jamais « voir » autre chose.
 */
function computeEffectiveRoles(real: EauRoles, sim: EauRole | null): EauRoles {
  if (real.admin && sim) {
    return {
      admin: sim === 'admin',
      releveur: sim === 'releveur',
      client: sim === 'client',
      promoteur: sim === 'promoteur',
      releveurElec: sim === 'releveurElec',
      gestionElec: sim === 'gestionElec',
      lectureElec: sim === 'lectureElec',
      validationElec: sim === 'validationElec',
    };
  }
  return real;
}

/** Notifie l'utilisateur du résultat d'un enrôlement traité au retour de Google. */
function notifyEnrollment(res: Awaited<ReturnType<typeof processPendingEnrollment>>): void {
  switch (res.kind) {
    case 'code-ok':
      toast.success(
        `Accès activé ✅ ${res.compteurCount} compteur(s) associé(s) à votre compte.`,
        { duration: 5000 }
      );
      break;
    case 'code-invalide':
      toast.error('Code d’enrôlement invalide. Vérifiez auprès de l’administrateur.', { duration: 6000 });
      break;
    case 'code-deja-utilise':
      toast.error('Ce code est déjà rattaché à un autre compte.', { duration: 6000 });
      break;
    case 'demande-ok':
      toast.success('Demande d’accès envoyée. Un administrateur va la traiter.', { duration: 6000 });
      break;
    default:
      break;
  }
}

export const GestionEauContext = createContext<GestionEauContextType | undefined>(undefined);

interface ProviderProps {
  children: ReactNode;
}

export const GestionEauProvider: React.FC<ProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const storeUser = useAppStore((s) => s.user);
  const isOnline = useAppStore((s) => s.isOnline);
  const [userId, setUserId] = useState<string | null>(null);
  // Rôles RÉELS (résolus serveur/cache). Les rôles effectifs (avec simulation) sont
  // dérivés au rendu — voir `computeEffectiveRoles`.
  const [realRoles, setRealRoles] = useState<EauRoles>(EMPTY_ROLES);
  // Simulation de rôle (admin uniquement, 100 % frontend). null = pas de simulation.
  const [simulatedRole, setSimulatedRoleState] = useState<EauRole | null>(null);
  const [simulatedClient, setSimulatedClient] = useState<EauSimulatedClient | null>(null);
  // Domaine choisi (ELEC-CC-01), mémorisé par utilisateur : `eau_domaine_<userId>`.
  const [storedDomaine, setStoredDomaine] = useState<string | null>(null);
  // Domaines du compte en tant que client (eau / électricité RTB), lus localement (ELEC-CC-02).
  const [clientDomaines, setClientDomaines] = useState<EauDomaines>({ eau: false, elec: false });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sessionStatus, setSessionStatus] = useState<EauSessionStatus>('checking');
  // Fiabilité de la résolution des rôles (cf. RolesResolution.confirmed). false au boot :
  // tant que le pull serveur n'a pas répondu, on ne conclut pas « aucun accès ».
  const [rolesConfirmed, setRolesConfirmed] = useState(false);
  // Le spinner bloquant ne doit s'afficher qu'au TOUT PREMIER chargement.
  // Les rechargements suivants (bascule online/offline, fréquente sur réseau instable)
  // se font en arrière-plan SANS démonter les écrans → pas de flash ni de perte de saisie.
  const initialLoadDoneRef = useRef(false);

  const load = useCallback(async (showSpinner: boolean) => {
    if (showSpinner) setIsLoading(true);
    setError(null);
    try {
      const online = typeof navigator === 'undefined' ? true : navigator.onLine;

      // ── Étape B : garantir une session Supabase FIABLE = utilisateur courant du shell.
      // On attend la session (absorbe la course au boot ; lecture localStorage, pas de
      // réseau). On NE crée jamais une 2ᵉ identité et on NE déconnecte jamais.
      const session = await waitForEauSession(online ? 6 : 2);
      const storeId = useAppStore.getState().user?.id ?? null;
      const sessionUserId = session?.user?.id ?? null;

      let id: string | null;
      if (sessionUserId) {
        if (storeId && sessionUserId !== storeId) {
          // Compte Google différent du compte connecté au shell → refuser.
          setSessionStatus('mismatch');
          setUserId(storeId);
          setRealRoles(EMPTY_ROLES);
          if (showSpinner) setIsLoading(false);
          return;
        }
        id = sessionUserId; // identité fiable (uid == users.id du shell)
        setSessionStatus('valid');
      } else if (storeId && !online) {
        // Hors-ligne sans session lisible MAIS une session avait été établie sur
        // l'appareil (store rehydraté) → continuer en lecture locale (Dexie).
        id = storeId;
        setSessionStatus('valid');
      } else {
        // En ligne sans session lisible (ou jamais établie hors-ligne) → ré-auth Google.
        setSessionStatus('needs-reauth');
        setUserId(storeId);
        setRealRoles(EMPTY_ROLES);
        if (showSpinner) setIsLoading(false);
        return;
      }

      setUserId(id);

      // Enrôlement en attente (retour de connexion Google) : lier un code ou créer
      // une demande d'accès, AVANT de lire les rôles (pour refléter le rôle client).
      if (getPendingEnrollment()) {
        try {
          const email = useAppStore.getState().user?.email ?? null;
          const res = await processPendingEnrollment(id, email);
          notifyEnrollment(res);
        } catch {
          /* best-effort */
        }
      }

      // Octroi automatique « invitation par email » (Phase 1) : si une invitation
      // `en_attente` cible l'adresse Google de ce compte, la RPC attribue les rôles
      // (et crée/active le compte client + compteurs). EN LIGNE uniquement et AVANT la
      // lecture des rôles, pour que `ensureRolesBootstrap` reflète le rôle fraîchement
      // accordé. Best-effort : un échec réseau ne casse pas le chargement.
      // Octroi « invitation vitrine WhatsApp par JETON » : id non null = un jeton vient
      // d'être consommé (rôle fraîchement accordé) → on atterrira sur la cible du rôle.
      let claimedTokenId: string | null = null;
      if (online) {
        await claimInvitationForCurrentUser(online).catch(() => null);
        // Octroi « invitation vitrine WhatsApp par JETON » : si un jeton a été capturé
        // (vitrine /i/<token>) et déposé en sessionStorage, la RPC attribue les rôles
        // (et crée/active le compte client). Best-effort, AVANT la lecture des rôles.
        claimedTokenId = await claimPendingTokenInvitation(online).catch(() => null);
      }

      // Rafraîchit config + données de base en arrière-plan (best-effort). On EXCLUT
      // `eau_roles`/`eau_comptes_client` : `ensureRolesBootstrap` (ci-dessous) les tire
      // déjà — une seule fois — évitant le double pull concurrent au démarrage (B-3).
      void refreshConfig(online).catch(() => {});
      void pullAll(['eau_roles', 'eau_comptes_client']).catch(() => {});

      // Mémento §17 (ELEC-CC-02) : au premier lancement en ligne de la 3.115.0, l'état
      // électricité local est vidé puis retiré du serveur (avant la lecture des rôles, qui
      // lit la fiche client électricité). Best-effort.
      await forceResyncElecUneFois(online).catch(() => false);

      // Bootstrap propriétaire + lecture des rôles effectifs (avec retry du pull à froid).
      const { roles: effective, confirmed } = await ensureRolesBootstrap(id, online);
      setRealRoles(effective);
      setClientDomaines(await getClientDomaines(id));
      setRolesConfirmed(confirmed);

      // Redirection post-claim (invitation vitrine WhatsApp) : un jeton vient d'être
      // consommé avec succès → atterrir sur la cible du rôle accordé (releveur/admin →
      // saisie bassin ; client → espace client). Évite de laisser l'invité sur la page
      // publique d'atterrissage. Ne se déclenche qu'UNE fois (le jeton est retiré du
      // sessionStorage au succès, donc les chargements suivants renvoient null).
      if (claimedTokenId) {
        const target = invitationTargetPath({
          role_admin: effective.admin,
          role_releveur: effective.releveur,
          role_client: effective.client,
          role_promoteur: effective.promoteur,
          role_releveur_elec: effective.releveurElec,
          role_gestion_elec: effective.gestionElec,
          role_lecture_elec: effective.lectureElec,
          role_validation_elec: effective.validationElec,
        });
        navigate(target);
      }
    } catch (e: any) {
      console.error('❌ [GestionEau] Erreur chargement contexte:', e);
      setError(e?.message ?? 'Erreur de chargement du module Gestion Eau');
      setRealRoles(EMPTY_ROLES);
      // Erreur = état NON résolu : la garde affichera un écran d'attente, pas un rebond.
      setRolesConfirmed(false);
    } finally {
      if (showSpinner) setIsLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    // Spinner uniquement au premier chargement ; rechargements (réseau/login) en arrière-plan.
    const showSpinner = !initialLoadDoneRef.current;
    initialLoadDoneRef.current = true;
    load(showSpinner);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeUser?.id, isOnline]);

  // Déclencheur de SYNC au retour en ligne : vide la file `_dirty` (relevés, compteurs,
  // QR, scans… créés hors-ligne) puis tire les nouveautés serveur. Idempotent (upsert id
  // client) → aucun doublon même si un envoi hors-ligne avait déjà été commité. Best-effort.
  const wasOnlineRef = useRef(isOnline);
  useEffect(() => {
    const cameOnline = !wasOnlineRef.current && isOnline;
    wasOnlineRef.current = isOnline;
    if (cameOnline) {
      void syncAll().catch(() => {});
    }
  }, [isOnline]);

  const refreshRoles = useCallback(async () => {
    if (!userId) return;
    const effective = await getRolesForUser(userId);
    setRealRoles(effective);
    setClientDomaines(await getClientDomaines(userId));
  }, [userId]);

  // Relance complète (session + bootstrap + pull rôles) avec spinner. Utilisé par
  // l'écran d'attente lorsque la résolution à froid a échoué (« Réessayer »).
  const retryAccess = useCallback(async () => {
    await load(true);
  }, [load]);

  // Reconnexion Google depuis le module (même identité que le shell). La redirection
  // OAuth est gérée par authService/AuthPage ; au retour, le statut repassera à 'valid'.
  const reauth = useCallback(async () => {
    try {
      await authService.signInWithGoogle();
    } catch (e: any) {
      console.error('❌ [GestionEau] Échec reconnexion Google:', e);
      toast.error('Reconnexion impossible pour le moment. Réessayez.', { duration: 5000 });
    }
  }, []);

  // ───────────────────────── Simulation de rôle (admin) ─────────────────────────
  // Restauration au montage : SEULEMENT après confirmation fiable des rôles ET si
  // l'utilisateur réel est admin. Pour un non-admin, on purge toute valeur résiduelle
  // (défense en profondeur : la simulation ne doit jamais s'appliquer à un non-admin).
  const simRestoredRef = useRef(false);
  useEffect(() => {
    if (!rolesConfirmed) return;
    if (!realRoles.admin) {
      if (simulatedRole !== null) setSimulatedRoleState(null);
      try {
        localStorage.removeItem(SIM_ROLE_KEY);
        localStorage.removeItem(SIM_CLIENT_KEY);
      } catch {
        /* localStorage indisponible */
      }
      return;
    }
    if (simRestoredRef.current) return;
    simRestoredRef.current = true;
    try {
      const stored = localStorage.getItem(SIM_ROLE_KEY) as EauRole | null;
      if (stored && SIMULATABLE_ROLES.includes(stored)) {
        if (stored === 'client') {
          // Le rôle Propriétaire n'a de sens qu'avec sa villa (périmètre de données) :
          // restaurer les deux ensemble, sinon purger (pas de simulation client « vide »).
          const client = parseSimClient(localStorage.getItem(SIM_CLIENT_KEY));
          if (client) {
            setSimulatedRoleState('client');
            setSimulatedClient(client);
          } else {
            localStorage.removeItem(SIM_ROLE_KEY);
            localStorage.removeItem(SIM_CLIENT_KEY);
          }
        } else {
          setSimulatedRoleState(stored);
        }
      } else if (stored) {
        localStorage.removeItem(SIM_ROLE_KEY);
      }
    } catch {
      /* localStorage indisponible */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rolesConfirmed, realRoles.admin]);

  // Purge à la déconnexion (logout / SIGNED_OUT) : détectée par la transition
  // « avait un utilisateur » → « plus d'utilisateur ». On ne purge PAS le null de boot
  // (ref initialisée à false) pour ne pas effacer une simulation légitime avant le login.
  const hadUserRef = useRef(false);
  useEffect(() => {
    const uid = storeUser?.id ?? null;
    if (uid) {
      hadUserRef.current = true;
    } else if (hadUserRef.current) {
      hadUserRef.current = false;
      simRestoredRef.current = false;
      setSimulatedRoleState(null);
      setSimulatedClient(null);
      try {
        localStorage.removeItem(SIM_ROLE_KEY);
        localStorage.removeItem(SIM_CLIENT_KEY);
      } catch {
        /* localStorage indisponible */
      }
    }
  }, [storeUser?.id]);

  // Incarner un rôle : réservé à l'admin réel + rôle simulable (Phase 1 : releveur/promoteur).
  const setSimulation = useCallback(
    (role: EauRole, client: EauSimulatedClient | null = null) => {
      if (!realRoles.admin) {
        console.warn('⚠️ [EauSim] Simulation réservée à l’administrateur. Ignoré.');
        return;
      }
      if (!SIMULATABLE_ROLES.includes(role)) {
        console.warn('⚠️ [EauSim] Rôle non simulable :', role);
        return;
      }
      if (role === 'client' && !client) {
        // Propriétaire sans villa = aucun périmètre de données → refuser (le sélecteur
        // impose de choisir une villa avant d'appeler setSimulation('client', villa)).
        console.warn('⚠️ [EauSim] Simulation « Propriétaire » sans villa choisie. Ignoré.');
        return;
      }
      setSimulatedRoleState(role);
      setSimulatedClient(client);
      try {
        localStorage.setItem(SIM_ROLE_KEY, role);
        if (client) localStorage.setItem(SIM_CLIENT_KEY, JSON.stringify(client));
        else localStorage.removeItem(SIM_CLIENT_KEY);
      } catch {
        /* localStorage indisponible : la simulation reste en mémoire pour cette session */
      }
    },
    [realRoles.admin]
  );

  // Relecture du domaine mémorisé dès que l'utilisateur est connu.
  useEffect(() => {
    if (!userId) return;
    try {
      setStoredDomaine(localStorage.getItem(domaineStorageKey(userId)));
    } catch {
      /* localStorage indisponible */
    }
  }, [userId]);

  const setDomaineActif = useCallback(
    (d: EauDomaine) => {
      setStoredDomaine(d);
      if (!userId) return;
      try {
        localStorage.setItem(domaineStorageKey(userId), d);
      } catch {
        /* localStorage indisponible : le choix reste en mémoire */
      }
    },
    [userId]
  );

  const clearSimulation = useCallback(() => {
    setSimulatedRoleState(null);
    setSimulatedClient(null);
    try {
      localStorage.removeItem(SIM_ROLE_KEY);
      localStorage.removeItem(SIM_CLIENT_KEY);
    } catch {
      /* localStorage indisponible */
    }
  }, []);

  // Rôles EFFECTIFS (avec simulation) — consommés par les gardes/nav/écrans.
  const roles = computeEffectiveRoles(realRoles, simulatedRole);
  const isSimulating = !!simulatedRole && realRoles.admin;

  const hasEauAccess =
    roles.admin ||
    roles.releveur ||
    roles.client ||
    roles.promoteur ||
    roles.releveurElec ||
    roles.gestionElec ||
    roles.lectureElec ||
    roles.validationElec;
  // Promoteur « pur » = lecture seule. Un admin/releveur (même cumulé promoteur) écrit.
  // Calculé sur les rôles EFFECTIFS → en simulation promoteur, l'admin passe en lecture seule.
  const isReadOnly = roles.promoteur && !roles.admin && !roles.releveur;

  // Domaines (ELEC-CC-01) sur les rôles EFFECTIFS : la simulation applique les domaines
  // comme pour un vrai compte. En simulation « Propriétaire », la villa simulée est un
  // client d'eau ; sinon les domaines client réels (eau, électricité RTB : ELEC-CC-02).
  const domaines = domainesPourRoles(roles, isSimulating ? undefined : clientDomaines);
  const domaineActif = resolveDomaineActif(domaines, storedDomaine);

  // Pastille du valideur (ELEC-CC-03) : lecture locale, relue toutes les 10 s et après
  // chaque action sur les factures.
  const [navBadges, setNavBadges] = useState<Record<string, number>>({});
  const estValideur = roles.validationElec;
  useEffect(() => {
    if (!estValideur || !userId) {
      setNavBadges({});
      return;
    }
    let vivant = true;
    const relire = async () => {
      try {
        const [f, a] = await Promise.all([
          eauDb.eau_elec_factures.toArray() as Promise<ElecFactureLocal[]>,
          eauDb.eau_elec_validations.toArray() as Promise<ElecValidationLocal[]>,
        ]);
        if (vivant) setNavBadges({ [ELEC_FACTURES]: nbAValiderSansAvis(f, a, userId) });
      } catch {
        /* lecture locale impossible : pas de pastille */
      }
    };
    void relire();
    const id = window.setInterval(() => void relire(), 10000);
    window.addEventListener(EAU_FACTURES_EVENT, relire);
    return () => {
      vivant = false;
      window.clearInterval(id);
      window.removeEventListener(EAU_FACTURES_EVENT, relire);
    };
  }, [estValideur, userId]);

  // Périmètre de données de la simulation « Propriétaire » : compteurs de la villa simulée.
  // Non-null uniquement quand un admin incarne le rôle `client` avec une villa choisie.
  const dataScope =
    isSimulating && simulatedRole === 'client' && simulatedClient
      ? { compteurIds: simulatedClient.compteurIds }
      : null;

  return (
    <GestionEauContext.Provider
      value={{
        userId,
        roles,
        realRoles,
        isSimulating,
        simulatedRole,
        simulatedClient,
        dataScope,
        setSimulation,
        clearSimulation,
        hasEauAccess,
        domaines,
        domaineActif,
        setDomaineActif,
        isReadOnly,
        isLoading,
        error,
        sessionStatus,
        rolesConfirmed,
        refreshRoles,
        retryAccess,
        reauth,
        navBadges,
      }}
    >
      {children}
    </GestionEauContext.Provider>
  );
};

export const useGestionEau = (): GestionEauContextType => {
  const ctx = useContext(GestionEauContext);
  if (ctx === undefined) {
    throw new Error('useGestionEau doit être utilisé à l\'intérieur d\'un GestionEauProvider');
  }
  return ctx;
};
